"use client";

import { ChevronLeft, ChevronRight, Heart, ShoppingBag } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { HomeProduct } from "@/src/data/home";

type NewArrivalsCarouselProps = {
  products: HomeProduct[];
};

const euroFormatter = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
});

function ProductThumb({ src, title }: { src: string; title: string }) {
  const [failed, setFailed] = useState(false);

  return !src || failed ? (
    <div className="grid h-full w-full place-items-center bg-slate-100 p-4 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
      {title}
    </div>
  ) : (
    <img
      src={src}
      alt={title}
      loading="lazy"
      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
      onError={() => setFailed(true)}
    />
  );
}

function SkeletonRail() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <div
          key={`skeleton-${index}`}
          className="animate-pulse rounded-2xl border border-slate-200/80 bg-white/82 p-3"
        >
          <div className="h-48 rounded-xl bg-slate-200/85" />
          <div className="mt-4 h-3 w-1/2 rounded bg-slate-200/95" />
          <div className="mt-2 h-5 w-4/5 rounded bg-slate-200/85" />
          <div className="mt-4 h-11 rounded-xl bg-slate-200/95" />
        </div>
      ))}
    </div>
  );
}

export function NewArrivalsCarousel({ products }: NewArrivalsCarouselProps) {
  const [isLoading, setIsLoading] = useState(true);
  const railRef = useRef<HTMLDivElement | null>(null);

  const productList = useMemo(() => products.slice(0, 9), [products]);

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setIsLoading(false);
    }, 750);

    return () => window.clearTimeout(timerId);
  }, []);

  const scrollCards = (direction: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) {
      return;
    }

    rail.scrollBy({
      left: rail.clientWidth * 0.82 * direction,
      behavior: "smooth",
    });
  };

  if (isLoading) {
    return <SkeletonRail />;
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="home-focus inline-flex h-10 w-10 items-center justify-center rounded-full border border-cyan-500/32 bg-white/76 text-slate-700 transition hover:border-cyan-500/52 hover:bg-cyan-100/75"
          onClick={() => scrollCards(-1)}
          aria-label="Zurück"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          className="home-focus inline-flex h-10 w-10 items-center justify-center rounded-full border border-cyan-500/32 bg-white/76 text-slate-700 transition hover:border-cyan-500/52 hover:bg-cyan-100/75"
          onClick={() => scrollCards(1)}
          aria-label="Weiter"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div
        ref={railRef}
        className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto pb-3"
      >
        {productList.map((product) => (
          <article
            key={product.id}
            className="group relative min-w-[84%] snap-start overflow-hidden rounded-2xl border border-slate-200/80 bg-white/80 p-3 shadow-[0_16px_30px_rgba(30,41,59,0.12)] transition duration-300 hover:-translate-y-1 hover:border-emerald-500/35 hover:shadow-[0_22px_42px_rgba(16,185,129,0.2)] sm:min-w-[48%] lg:min-w-[30%]"
          >
            <span className="pointer-events-none absolute -right-8 -top-10 h-24 w-24 rounded-full bg-emerald-300/24 blur-2xl transition group-hover:bg-emerald-300/34" />
            <div className="relative h-48 overflow-hidden rounded-xl border border-slate-200/80 bg-slate-100">
              <ProductThumb src={product.image} title={product.title} />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white/78 via-white/16 to-transparent" />
              <span
                className={`absolute left-3 top-3 z-10 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] shadow-sm backdrop-blur-sm ${
                  product.inStock
                    ? "border-emerald-200/85 bg-emerald-500/90 text-white"
                    : "border-amber-100/85 bg-amber-500/92 text-white"
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    product.inStock ? "bg-emerald-100" : "bg-amber-100"
                  }`}
                />
                {product.stockLabel}
              </span>
              <span className="absolute bottom-3 right-3 rounded-full border border-slate-300/78 bg-white/84 px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] text-slate-600">
                {product.highlight}
              </span>
            </div>

            <div className="mt-4 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.12em] text-cyan-700">
                    {product.category}
                  </p>
                  <a
                    href={product.href}
                    className="home-focus mt-1 inline-flex text-base font-semibold text-slate-900 transition hover:text-emerald-700"
                  >
                    {product.title}
                  </a>
                </div>
                <p className="text-base font-semibold text-emerald-700">
                  {euroFormatter.format(product.price)}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="home-focus inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300/78 bg-white/84 text-slate-700 transition hover:border-emerald-500/40 hover:bg-emerald-100/80"
                  aria-label={`Merken ${product.title}`}
                >
                  <Heart className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className="home-focus inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300/78 bg-white/84 text-slate-700 transition hover:border-cyan-500/40 hover:bg-cyan-100/80"
                  aria-label={`In den Warenkorb ${product.title}`}
                >
                  <ShoppingBag className="h-4 w-4" />
                </button>
                <a
                  href={product.href}
                  className="home-focus inline-flex h-9 flex-1 items-center justify-center rounded-lg border border-cyan-500/35 bg-cyan-100/75 px-4 text-sm font-semibold text-cyan-900 transition hover:border-cyan-500/55 hover:bg-cyan-100"
                >
                  Schnell ansehen
                </a>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
